import React, { useState } from 'react'

import { Button } from '@clientbase/clientbase-library'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { Box, Menu, MenuItem } from '@mui/material'

import { theme } from 'styles/theme'

import type { MobileCardColumn } from './MobileCardList'
import type {
  MultiSortConfig,
  SortableColumn,
  SortConfig
} from './SortableTableHead'
import { Order } from './Table.interface'

/**
 * Estilo da linha de botoes da barra de filtros que hospeda o `compact`: faz os
 * controles dividirem a largura em partes iguais, para as bordas baterem com as
 * outras faixas da barra.
 *
 * Recebe `isMobile` e devolve `undefined` no desktop **de proposito**. A versao
 * anterior aplicava a regra sem gate e ela alcancava o desktop, onde
 * "Selecionar" e "Filtros" deixavam de ser `fit-content` e passavam a esticar —
 * efeito colateral numa mudanca que era so de mobile. Manter a decisao aqui,
 * numa funcao, e o que permite travar isso em teste.
 */
export const mobileToolbarRowSx = (isMobile: boolean) =>
  isMobile ? { '& > *': { flex: '1 1 0', minWidth: 'fit-content' } } : undefined

export interface MobileSortControlProps {
  /** Colunas do card, na ordem, com rótulo em texto puro. */
  columns: MobileCardColumn[]
  /** Quais colunas o backend aceita ordenar. */
  sortableColumns?: SortableColumn[]
  /** Ordenação atual (única ou multi-coluna). */
  sortConfig?: SortConfig | MultiSortConfig
  onSort?: (field: string, direction: Order) => void
  onMultiSort?: (sorts: MultiSortConfig) => void
  disableSort?: boolean
  /**
   * Versão para a barra de filtros: rótulo fixo "Ordenar", sem o nome da coluna
   * ativa. Ali o chip de "Ordenar: X" logo abaixo já diz qual é a coluna, e um
   * rótulo que muda de tamanho empurraria a largura da barra a cada troca.
   */
  compact?: boolean
}

/**
 * Substitui, no mobile, a ordenação que no desktop mora no cabeçalho da tabela:
 * o card não tem cabeçalho para clicar. Um botão abre a lista de colunas
 * ordenáveis e uma seta ao lado inverte a direção.
 *
 * Usa o mesmo `Button variantButton="neutral"` dos botões de filtro da barra
 * ("Selecionar", "Filtros: 0"), para o controle não parecer de outra família.
 * Sem `children` esse Button já vira um quadrado 36x36 com ícone — é o botão
 * da seta.
 *
 * Aparece apenas quando a tabela declara `sortableColumns` — ou seja, quando a
 * ordenação é feita no backend e realmente funciona. As tabelas antigas que
 * ordenam no cliente comparam ReactNode e não entram aqui.
 */
const MobileSortControl: React.FC<MobileSortControlProps> = ({
  columns,
  sortableColumns = [],
  sortConfig,
  onSort,
  onMultiSort,
  disableSort = false,
  compact = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const sortableMap = new Map(sortableColumns.map((col) => [col.id, col]))

  // Coluna ordenável precisa de rótulo: a de ações é ordenável em nenhum caso,
  // mas uma coluna sem texto não tem como ser oferecida numa lista.
  const options = columns.filter(
    (column) => sortableMap.get(column.id)?.sortable && column.label.trim()
  )

  const fieldOf = (column: MobileCardColumn) =>
    sortableMap.get(column.id)?.sortField || column.id

  const sortArray: MultiSortConfig = Array.isArray(sortConfig)
    ? sortConfig
    : sortConfig?.field
    ? [sortConfig]
    : []

  // No mobile a ordenação é sempre de uma coluna só: empilhar critérios num
  // menu de celular não se sustenta. O primeiro item e o que vale.
  const active = sortArray[0]
  const activeColumn = active
    ? options.find((column) => fieldOf(column) === active.field)
    : undefined

  if (disableSort || !options.length || (!onSort && !onMultiSort)) return null

  const apply = (field: string, direction: Order) => {
    if (onMultiSort) {
      onMultiSort(direction ? [{ field, direction }] : [])
      return
    }
    onSort?.(field, direction)
  }

  const handlePick = (column: MobileCardColumn) => {
    setAnchorEl(null)
    const field = fieldOf(column)
    // Reescolher a coluna ativa inverte a direção, como o clique no cabeçalho.
    if (active?.field === field) {
      apply(field, active.direction === 'asc' ? 'desc' : 'asc')
      return
    }
    apply(field, 'asc')
  }

  const toggleDirection = () => {
    if (!active) return
    apply(active.field, active.direction === 'asc' ? 'desc' : 'asc')
  }

  const clearSort = () => {
    setAnchorEl(null)
    if (!active) return
    apply(active.field, undefined)
  }

  const content = (
    <>
      <Button
        aria-controls={anchorEl ? 'mobile-sort-menu' : undefined}
        aria-expanded={anchorEl ? 'true' : undefined}
        aria-haspopup="true"
        endIcon="arrowDropDown"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{ minWidth: 'fit-content' }}
        variantButton="neutral"
      >
        {!compact && activeColumn
          ? `Ordenar: ${activeColumn.label}`
          : 'Ordenar'}
      </Button>

      {/* Sem Tooltip de propósito: o `Button` da lib é um function component
          sem `forwardRef`, então o Tooltip do MUI não consegue anexar a ref e
          nem apareceria — e num controle que só existe no mobile, tooltip de
          hover não serve. O `aria-label` carrega o significado, e a direção já
          está no próprio ícone. */}
      {!!active && (
        <Button
          aria-label={
            active.direction === 'asc'
              ? 'Ordem crescente, inverter'
              : 'Ordem decrescente, inverter'
          }
          onClick={toggleDirection}
          startIcon={
            active.direction === 'asc' ? 'arrowUpward' : 'arrowDownward'
          }
          style={{ flex: '0 0 auto' }}
          variantButton="neutral"
        />
      )}

      <Menu
        id="mobile-sort-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map((column) => {
          const isActive = active?.field === fieldOf(column)
          return (
            <MenuItem
              key={column.id}
              selected={isActive}
              onClick={() => handlePick(column)}
              sx={{ fontSize: '14px', gap: '10px', minWidth: '200px' }}
            >
              <Box flex={1}>{column.label}</Box>
              {isActive &&
                (active?.direction === 'asc' ? (
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                ))}
            </MenuItem>
          )
        })}
        {!!active && (
          <MenuItem
            onClick={clearSort}
            sx={{
              fontSize: '14px',
              gap: '10px',
              color: theme.palette.neutral[400],
              borderTop: `1px solid ${theme.palette.neutral[700]}`
            }}
          >
            Sem ordenação
          </MenuItem>
        )}
      </Menu>
    </>
  )

  // Na barra de filtros os botões precisam ser itens diretos da linha, para
  // ela distribuir a largura entre "Selecionar", "Filtros" e "Ordenar" por
  // igual. Um wrapper aqui viraria um único item e a linha ficaria curta,
  // desalinhada das faixas de cima e de baixo.
  if (compact) return content

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      gap="6px"
      marginBottom="12px"
    >
      {content}
    </Box>
  )
}

export default MobileSortControl
